import "./i18n";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CountryProvider } from "./context/CountryContext";
import { ZoneProvider } from "./context/ZoneContext";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import PassengerDashboard from "./pages/PassengerDashboard";
import DriverDashboard from "./pages/DriverDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import RestaurateurOnboarding from "./pages/RestaurateurOnboarding";
import RestaurateurDashboard from "./pages/RestaurateurDashboard";
import RestaurateurInvitation from "./pages/RestaurateurInvitation";
import PublicRestaurant from "./pages/PublicRestaurant";
import FoodCheckout from "./pages/FoodCheckout";
import FoodOrderSuccess from "./pages/FoodOrderSuccess";
import FoodOrderTracking from "./pages/FoodOrderTracking";
import LivreurDashboard from "./pages/LivreurDashboard";
import MyOrders from "./pages/MyOrders";
import GlobalFoodOrderListener from "./components/GlobalFoodOrderListener";
import SplashScreen from "./components/SplashScreen";
import RestaurantsList from "./pages/RestaurantsList";
import NoldFoodHome from "./pages/NoldFoodHome";
import CuisineCommunePage from "./pages/CuisineCommunePage";
import { CarpoolSearch, CarpoolPublish, CarpoolTrip, CarpoolMine } from "./pages/Carpool";
import { CarpoolProfile, CarpoolMyBookings, CarpoolRate } from "./pages/CarpoolProfile";
import { CarpoolLanding, CarpoolTerms } from "./pages/CarpoolLanding";
import CarpoolAlerts from "./pages/CarpoolAlerts";
import CarpoolRoute from "./pages/CarpoolRoute";
import { isNoldDomain } from "./lib/domain";
import PublicContractVerify from "./pages/PublicContractVerify";
import SignContractByToken from "./pages/SignContractByToken";
import RecruitDriver from "./pages/RecruitDriver";
import AgentSpace from "./pages/AgentSpace";
import PayLanding from "./pages/PayLanding";
import PaySuccess from "./pages/PaySuccess";
import RideConfirmed from "./pages/RideConfirmed";
import Privacy from "./pages/Privacy";
import MentionsLegales from "./pages/MentionsLegales";
import TripTracker from "./pages/TripTracker";
import Forum from "./pages/Forum";
import { InstallBanner } from "./components/InstallBanner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { BrandMeta } from "./components/BrandMeta";
import PageviewTracker from "./components/PageviewTracker";

function Protected({ role, children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-4 border-[#D94F2B] border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to={`/auth?role=${role || "passenger"}`} replace />;
  if (role && user.role !== role && !(user.roles || []).includes(role) && !user.is_super_admin) {
    // Livreur Food (bike/delivery) → /livreur ; sinon /driver ; sinon la home du rôle.
    let dest = `/${user.role}`;
    if (user.role === "driver") {
      dest = (user.vehicle_type === "bike" || user.vehicle_type === "delivery") ? "/livreur" : "/driver";
    }
    return <Navigate to={dest} replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App min-h-screen">
      <HelmetProvider>
      <AuthProvider>
        <ZoneProvider>
        <CountryProvider>
          <Toaster position="top-center" richColors />
          <BrandMeta />
          <SplashScreen />
          <ErrorBoundary>
          <BrowserRouter>
            <PageviewTracker />
            <GlobalFoodOrderListener />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/inscription" element={<Auth />} />
              <Route path="/recruter" element={<RecruitDriver />} />
              <Route path="/pay" element={<PayLanding />} />
              <Route path="/pay/merci" element={<PaySuccess />} />
              <Route path="/equipe" element={<AgentSpace />} />
              <Route path="/agent" element={<AgentSpace />} />
              <Route path="/confidentialite" element={<Privacy />} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/suivi/:token" element={<TripTracker />} />
              <Route path="/forum" element={<Protected><Forum /></Protected>} />
              <Route path="/course/confirmee" element={<Protected role="passenger"><RideConfirmed /></Protected>} />
              <Route path="/passenger" element={<Protected role="passenger"><PassengerDashboard /></Protected>} />
              <Route path="/driver" element={<Protected role="driver"><DriverDashboard /></Protected>} />
              <Route path="/restaurateur/inscription" element={<RestaurateurOnboarding />} />
              <Route path="/restaurateur/invitation" element={<RestaurateurInvitation />} />
              <Route path="/partenaires" element={<RestaurateurOnboarding />} />
              <Route path="/partenaire" element={<RestaurateurOnboarding />} />
              <Route path="/devenir-partenaire" element={<RestaurateurOnboarding />} />
              <Route path="/nold-partenaire" element={<RestaurateurOnboarding />} />
              <Route path="/restaurateur" element={<Protected role="restaurateur"><RestaurateurDashboard /></Protected>} />
              <Route path="/restaurants" element={isNoldDomain() ? <NoldFoodHome /> : <RestaurantsList />} />
              <Route path="/restaurants/:id" element={<PublicRestaurant />} />
              {/* Pages de destination SEO longue traîne — 8 cuisines × 10 communes = 80 URL indexables */}
              <Route path="/livraison/:cuisine/:commune" element={<CuisineCommunePage />} />
              {/* Module Covoiturage longue distance style BlaBlaCar */}
              <Route path="/covoiturage" element={<CarpoolSearch />} />
              <Route path="/covoiturage/publier" element={<CarpoolPublish />} />
              <Route path="/covoiturage/mes-trajets" element={<CarpoolMine />} />
              <Route path="/covoiturage/trajet/:id" element={<CarpoolTrip />} />
              <Route path="/covoiturage/profil" element={<CarpoolProfile />} />
              <Route path="/covoiturage/mes-reservations" element={<CarpoolMyBookings />} />
              <Route path="/covoiturage/noter/:booking_id" element={<CarpoolRate />} />
              <Route path="/covoiturage/decouvrir" element={<CarpoolLanding />} />
              <Route path="/covoiturage/bruxelles-paris" element={<CarpoolRoute slug="bruxelles-paris" />} />
              <Route path="/covoiturage/paris-bruxelles" element={<CarpoolRoute slug="paris-bruxelles" />} />
              <Route path="/covoiturage/cgv" element={<CarpoolTerms />} />
              <Route path="/covoiturage/conditions" element={<CarpoolTerms />} />
              <Route path="/covoiturage/mes-alertes" element={<CarpoolAlerts />} />
              {/* Alias anglais /carpool → landing publique + sous-pages (SEO + tests + partages internationaux) */}
              <Route path="/carpool" element={<CarpoolLanding />} />
              <Route path="/carpool/search" element={<CarpoolSearch />} />
              <Route path="/carpool/publish" element={<CarpoolPublish />} />
              <Route path="/carpool/trip/:id" element={<CarpoolTrip />} />
              <Route path="/carpool/mine" element={<CarpoolMine />} />
              <Route path="/carpool/bookings" element={<CarpoolMyBookings />} />
              <Route path="/carpool/rate/:booking_id" element={<CarpoolRate />} />
              <Route path="/carpool/alerts" element={<CarpoolAlerts />} />
              <Route path="/carpool/terms" element={<CarpoolTerms />} />
              <Route path="/food/checkout" element={<FoodCheckout />} />
              <Route path="/food/merci" element={<FoodOrderSuccess />} />
              <Route path="/food/order/:orderId" element={<FoodOrderTracking />} />
              <Route path="/mes-commandes" element={<Protected><MyOrders /></Protected>} />
              <Route path="/livreur" element={<Protected role="driver"><LivreurDashboard /></Protected>} />
              <Route path="/verify/contract/:id" element={<PublicContractVerify />} />
              <Route path="/sign-contract/:token" element={<SignContractByToken />} />
              <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          </ErrorBoundary>
          <InstallBanner />
        </CountryProvider>
        </ZoneProvider>
      </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
